import { action, atom } from "@reatom/core"
import { generatePath } from "react-router-dom"

import { getPageOfSearchResults } from "modules/PsyCatalog/common/api"
import { Psychologist } from "utils/interfaces"
import { FormState } from "modules/PsyCatalog/common/searchQuery/schema"
import { Analytics } from "utils/analytics"
import { PAGE_SIZE } from "modules/PsyCatalog/browser-side"
import { getSubRoutes, getCurrentPage } from "modules/PsyCatalog/common/utils"
import {
    loadingAtom,
    searchRequestResultAtom,
} from "modules/PsyCatalog/common/models/searchRequest"
import { formAtom } from "modules/PsyCatalog/common/models/filterForm"
import { profilesCachedAtom } from "modules/PsyCatalog/common/models/profilesCache"
import { isBrowser } from "utils/infrastructure/ssg-utils"

export const paginationAtom = atom<{
    currentPage: number
    pagesCount: number
}>({
    currentPage: getCurrentPage(),
    pagesCount: 1,
})

export const goToNextPage = action((ctx) => {
    paginationAtom(ctx, (state) => {
        const next = state.currentPage + 1
        return {
            ...state,
            currentPage: next <= state.pagesCount ? next : state.currentPage,
        }
    })
    updateUrlWithPageNumber(ctx.get(paginationAtom).currentPage)
    void runGetPage(ctx)
})

export const goToPrevPage = action((ctx) => {
    paginationAtom(ctx, (state) => {
        const prev = state.currentPage - 1
        return {
            ...state,
            currentPage: prev >= 1 ? prev : state.currentPage,
        }
    })
    updateUrlWithPageNumber(ctx.get(paginationAtom).currentPage)
    void runGetPage(ctx)
})

export const goToPage = action((ctx, value: number) => {
    paginationAtom(ctx, (state) => ({
        ...state,
        currentPage: value > 0 && value <= state.pagesCount ? value : state.currentPage,
    }))
    void runGetPage(ctx, value)
})

export const goToPageAndUpdateUrl = action((ctx, value: number) => {
    if (ctx.get(paginationAtom).currentPage === value) return
    goToPage(ctx, value)
    updateUrlWithPageNumber(ctx.get(paginationAtom).currentPage)
})

const runGetPage = action((ctx, value?: number) => {
    const page = value ?? ctx.get(paginationAtom).currentPage
    const search = ctx.get(searchRequestResultAtom)
    const profilesCached = ctx.get(profilesCachedAtom)
    const pageIndex = page - 1
    if (!search) {
        loadingAtom(ctx, "idle")
        return Promise.resolve()
    }
    const hashIds = search.pages[pageIndex]
    if (!hashIds) {
        loadingAtom(ctx, "empty")
        return Promise.resolve()
    }
    loadingAtom(ctx, "loading")
    return ctx.schedule(async () => {
        try {
            if (profilesCached[pageIndex]) {
                searchRequestResultAtom(ctx, (data) => {
                    return {
                        ...data,
                        profiles: profilesCached[pageIndex],
                    }
                })
                loadingAtom(ctx, "success")
            } else {
                const { profiles } = await getPageOfSearchResults(hashIds)
                if (profiles.length == 0) {
                    loadingAtom(ctx, "empty")
                } else {
                    ctx.get(() => {
                        profilesCachedAtom(ctx, (data) => {
                            return {
                                ...data,
                                [pageIndex]: profiles,
                            }
                        })
                        searchRequestResultAtom(ctx, (data) => {
                            return {
                                ...data,
                                profiles,
                            }
                        })
                        if (profiles.length > 0) {
                            loadingAtom(ctx, "success")
                            sendPageAnalytics({ page, formState: ctx.get(formAtom), profiles })
                        } else {
                            loadingAtom(ctx, "empty")
                        }
                    })
                }
            }
        } catch (error) {
            loadingAtom(ctx, "error")
        }
    })
})

function sendPageAnalytics({
    page,
    formState,
    profiles,
}: {
    page: number
    profiles: Psychologist[]
    formState: FormState
}) {
    Analytics.page_shown({ page, ...formState })
    profiles.forEach((psy, index) => {
        Analytics.in_search_results({
            psy,
            position: `${index + 1 + PAGE_SIZE * (page - 1)}`,
        })
    })
}

function updateUrlWithPageNumber(pageNumber: number) {
    if (!isBrowser()) return
    const { routes, params } = getSubRoutes(location.pathname)
    const newParams: { page: string | null; filter: string | null } = {
        page: params && "page" in params ? String(params.page) : null,
        filter: params && "filter" in params ? String(params.filter) : null,
    }

    if (pageNumber !== 1) {
        newParams["page"] = String(pageNumber)
    }

    const pathname = generatePath(pageNumber === 1 ? routes.path : routes.withPageNumber, newParams)

    window.history.pushState(
        {},
        "",
        new URL(pathname + window.location.search, window.location.origin)
    )
}
